import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import { Tooltip } from '@material-ui/core';
import IconImageFill from 'assets/Icons/IconImageFill';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import makeStyles from '@material-ui/styles/makeStyles';
import {
  grayColor,
  primaryColor,
} from 'assets/jss/material-dashboard-pro-react';
import IconClose from 'assets/Icons/IconClose';
import axios from 'helpers/gastro';

const useStyles = makeStyles(() => ({
  photoButton: {
    cursor: 'pointer',
    color: primaryColor[0],
  },
  photoButtonWrapper: {
    display: 'inline-block',
    textAlign: 'center',
    width: '100%',
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  iconTitle: {
    color: grayColor[2],
    width: '24px',
    height: '24px',
  },
  buttonClose: {
    marginLeft: 'auto',
    cursor: 'pointer',
    margin: '-20px',
    padding: '20px',
    background: 'none',
    border: 'none',
  },
  photo: {
    maxHeight: 'calc(90vh - 160px)',
    maxWidth: 'calc(90vw - 48px)',
    width: 'auto',
    height: 'auto',
  },
}));

type DeliveryPhotoProps = {
  src: string;
  id: number | string;
};

const DeliveryPhoto = ({ src, id }: DeliveryPhotoProps) => {
  const { t } = useTranslation();
  const [isOpened, setIsOpened] = useState(false);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);

  const classes = useStyles();

  useEffect(() => {
    if (!isOpened || !src) {
      return undefined;
    }

    let isCancelled = false;
    let objectUrl: string | null = null;

    const load = async () => {
      try {
        const res = await axios.get(src, {
          responseType: 'blob',
          disableLoader: true,
        } as Parameters<typeof axios.get>[1]);

        objectUrl = URL.createObjectURL(res.data);

        if (isCancelled) {
          // Dialog was closed or src changed before the request finished.
          URL.revokeObjectURL(objectUrl);
          return;
        }

        setBlobUrl(prev => {
          if (prev) {
            URL.revokeObjectURL(prev);
          }
          return objectUrl;
        });
      } catch {
        if (!isCancelled) {
          setBlobUrl(null);
        }
      }
    };

    load();

    return () => {
      isCancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [isOpened, src]);

  useEffect(
    () => () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    },
    [blobUrl]
  );

  const handleClose = () => {
    setIsOpened(false);
    setBlobUrl(null);
  };

  return (
    <>
      <div className={classes.photoButtonWrapper}>
        <Tooltip
          title={t('ecommerceBags.fields.bag.showPhoto')}
          placement="top"
        >
          <span
            onClick={() => setIsOpened(true)}
            className={classes.photoButton}
          >
            <IconImageFill />
          </span>
        </Tooltip>
      </div>
      <Dialog open={isOpened} onClose={handleClose} maxWidth={false}>
        <DialogTitle>
          <div className={classes.title}>
            <IconImageFill className={classes.iconTitle} />
            {t('ecommerceBags.fields.bag.deliveryPhotoModalTitle', { id })}
            <button onClick={handleClose} className={classes.buttonClose}>
              <IconClose />
            </button>
          </div>
        </DialogTitle>
        <DialogContent>
          {blobUrl && <img src={blobUrl} className={classes.photo} alt="" />}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default DeliveryPhoto;
